import React from 'react';
import { Eye, Wrench, Layers, FileText } from 'lucide-react';

export type ActiveTab = 'visualizer' | 'teardown' | 'bom' | 'dossier';

export interface NavigationTabsProps {
  activeTab: ActiveTab;
  onSelectTab: (tab: ActiveTab) => void;
  teardownCount?: number;
  bomCount?: number;
}

export const NavigationTabs: React.FC<NavigationTabsProps> = ({
  activeTab,
  onSelectTab,
  teardownCount = 7,
  bomCount = 10,
}) => {
  const tabs: {
    id: ActiveTab;
    label: string;
    sublabel: string;
    icon: React.ReactNode;
    badge?: number | string;
  }[] = [
    {
      id: 'visualizer',
      label: 'Stance Visualizer',
      sublabel: '2D Paint & Wheels',
      icon: <Eye className="w-5 h-5" />,
    },
    {
      id: 'teardown',
      label: 'Teardown Tracker',
      sublabel: '7 Phases & Hardware',
      icon: <Wrench className="w-5 h-5" />,
      badge: `${teardownCount} Bins`,
    },
    {
      id: 'bom',
      label: 'Parts Procurement',
      sublabel: 'BOM Matrix & Budget',
      icon: <Layers className="w-5 h-5" />,
      badge: `${bomCount} Items`,
    },
    {
      id: 'dossier',
      label: 'Valuation Dossier',
      sublabel: 'Agreed-Value Report',
      icon: <FileText className="w-5 h-5" />,
      badge: 'Print Ready',
    },
  ];

  return (
    <nav
      role="tablist"
      aria-label="Workshop Navigation Tabs"
      className="app-tabs bg-shop-darkest border-b border-shop-border px-4 select-none shrink-0"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-2 overflow-x-auto no-scrollbar py-2">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelectTab(tab.id)}
              className={`min-h-[48px] px-4 py-2 flex items-center gap-3 rounded border transition-all duration-100 cursor-pointer text-left shrink-0 ${
                isActive
                  ? 'bg-shop-panel text-shop-highlight border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                  : 'bg-shop-dark text-shop-muted border-shop-border hover:text-shop-light hover:bg-shop-well'
              }`}
            >
              <span
                className={`p-1.5 rounded ${
                  isActive
                    ? 'bg-safety-amber text-shop-darkest'
                    : 'bg-shop-darkest text-shop-muted'
                }`}
              >
                {tab.icon}
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-bold uppercase tracking-wider ${
                      isActive ? 'text-shop-highlight' : 'text-shop-light'
                    }`}
                  >
                    {tab.label}
                  </span>
                  {tab.badge && (
                    <span
                      className={`text-[10px] font-mono font-bold px-1.5 py-0.2 rounded border ${
                        isActive
                          ? 'bg-shop-darkest text-safety-amber border-safety-amber'
                          : 'bg-shop-darkest text-shop-muted border-shop-border'
                      }`}
                    >
                      {tab.badge}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-shop-muted">{tab.sublabel}</div>
              </div>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
